/**
 * <TutorDecisionColumn> — la colonne de droite du live du masteur : **« À traiter »**, pour tous les élèves
 * (organism).
 *
 * Elle ne montre que ce qui attend le masteur, de toute la salle : d'abord les **signaux des élèves**
 * (`<TutorAlertRow>` : une main, une sortie de l'appli depuis 30 s, un silence — l'escalade en tête), puis les
 * **propositions**, une même carte chacune et la plus ancienne d'abord : un rendu à valider, un résumé du champion,
 * « <champion> propose », une demande du masteur en cours ou prête (`<TutorSessionEntry>`, tous ses gestes compris).
 * Aucun intertitre : la forme sépare les deux familles (des rangées teintées, puis des cartes) ; chacune garde un nom
 * pour un lecteur d'écran. Le compte de la tête est rouge seulement quand un signal attend ; vide, « Tout va bien ».
 *
 * Ce qui s'est passé n'est plus ici : c'est le fil de chaque élève, dans sa tuile, et l'onglet « Fil » au centre.
 * Un élève avec le champion n'y est pas : sa tuile le dit (l'aurore, le fil) ; seul un résumé qui demande le masteur
 * arrive ici.
 *
 * Changer d'élève se fait à gauche (sa tuile, la mosaïque) ou ici, par « Voir <prénom> » : il n'y a plus de puces
 * d'élèves en tête. Seul (un cours individuel), la colonne est réduite : pas de « tous les élèves », pas de prénom
 * sur les propositions.
 *
 * Elle est prête pour un champion qui agit seul : une carte qu'il signe (`sam: "acts"`) est une proposition comme une
 * autre, avec son action et sa croix.
 *
 * En bas, le champ (`<TutorDraftComposer>`) : générer pour l'élève regardé. Ancrée à partir de 1 024 px, en tiroir en
 * dessous (sa croix).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UITutorDecision, type UITutorDecisions } from "../../../ports/primary/ui/types/tutorLive"
import { Avatar } from "../../atoms/Avatar"
import { ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { Badge } from "../../atoms/Badge"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { type IconName } from "../../atoms/icons/Icon"
import { ScrollView } from "../../atoms/ScrollView"
import { Text } from "../../atoms/typography/Text"
import { EmptyState } from "../../molecules/EmptyState"
import { TutorAlertRow } from "../../molecules/TutorAlertRow"
import { TutorDraftComposer, type TutorSessionComposer } from "../../molecules/TutorDraftComposer"
import { TutorSessionFeedCard } from "../../molecules/TutorSessionFeedCard"
import { cappedCount } from "../../utils/cappedCount"
import { TutorSessionEntry, type TutorSessionEntryProps } from "../TutorSessionEntry"

export type TutorDecisionColumnProps = {
    /** Le champion qui aide en séance : sa tête signe la colonne et ses cartes. */
    helper: ChampionName
    decisions: UITutorDecisions
    /** Un cours individuel : la colonne réduite (ni « tous les élèves » ni prénom sur les propositions). */
    single?: boolean
    /** L'élève regardé : son « Voir » ne s'écrit pas. */
    selectedStudentId?: string | null
    onSelectStudent: (studentId: string) => void
    /** Une alerte ou une carte : son action, ou l'écarter. */
    onAction: (itemId: string, actionId: string) => void
    onDismiss: (itemId: string) => void
    pending?: { itemId: string; actionId: string }
    /** Les gestes d'une demande du masteur (prête, en cours, son résultat) et de « <champion> propose ». */
    onResult?: TutorSessionEntryProps["onResult"]
    onGeneration?: TutorSessionEntryProps["onGeneration"]
    onPeek?: TutorSessionEntryProps["onPeek"]
    peekId?: string | null
    composer?: TutorSessionComposer
    /** En tiroir (sous 1 024 px) : sa croix. */
    onClose?: () => void
    /**
     * La place que garde la tête à droite pour le chevron qui replie la colonne, posé par la page à la place exacte de
     * celui qui la déplie (`COLUMN_TOGGLE`). Défaut : aucune.
     */
    toggleRoom?: number
    testID?: string
}

const TEXTS = {
    title: "À traiter",
    scope: "tous les élèves",
    signals: "Signaux des élèves",
    proposals: "Propositions",
    forStudent: (firstname: string) => `Pour ${firstname}`,
    look: (firstname: string) => `Voir ${firstname}`,
    /** « Pour <prénom> » le nomme déjà. */
    lookShort: "Voir",
    calmTitle: "Tout va bien",
    calmMessage: "Les demandes des élèves et les propositions arriveront ici.",
    close: "Fermer « À traiter »",
}

const HEAD_SIZE = 28

/** Le compte de « À traiter » (les propositions comprises) et son accent (un signal d'élève attend) : l'en-tête et la cloche qui l'ouvre le lisent ici. */
export function decisionBadge({ alerts, proposals }: UITutorDecisions): { count: number; urgent: boolean } {
    return { count: alerts.length + proposals.length, urgent: alerts.length > 0 }
}

export function TutorDecisionColumn(props: TutorDecisionColumnProps) {
    const {
        helper,
        decisions,
        single = false,
        selectedStudentId,
        onSelectStudent,
        onAction,
        onDismiss,
        pending,
        onResult,
        onGeneration,
        onPeek,
        peekId,
        composer,
        onClose,
        toggleRoom,
        testID,
    } = props
    const { alerts, proposals } = decisions
    const badge = decisionBadge(decisions)
    const look = (studentId: string) =>
        single || studentId === selectedStudentId ? undefined : () => onSelectStudent(studentId)
    const pendingFor = (itemId: string) => (pending?.itemId === itemId ? pending.actionId : undefined)

    const proposal = (decision: UITutorDecision) => {
        if (decision.kind === "card") {
            const { item } = decision
            return (
                <TutorSessionFeedCard
                    key={decision.id}
                    item={item}
                    helper={helper}
                    onAction={(actionId) => onAction(item.id, actionId)}
                    onDismiss={() => onDismiss(item.id)}
                    onLook={look(item.student.id)}
                    pendingActionId={pendingFor(item.id)}
                />
            )
        }
        const { student } = decision
        const lookAt = student != null ? look(student.id) : undefined
        return (
            <Stack key={decision.id} gap="1">
                {!single && student != null && (
                    <Row gap="2" align="center">
                        <Avatar uri={student.avatarUrl} name={student.firstname} size={20} />
                        <Text size="text-xs" weight="bold" color="text-strong" numberOfLines={1} style={styles.flex}>
                            {TEXTS.forStudent(student.firstname)}
                        </Text>
                        <Text size="text-xxs" color="text-muted">
                            {formatTime(decision.at)}
                        </Text>
                        {lookAt != null && (
                            <TextButton
                                label={TEXTS.lookShort}
                                accessibilityLabel={TEXTS.look(student.firstname)}
                                size="sm"
                                onPress={lookAt}
                            />
                        )}
                    </Row>
                )}
                <TutorSessionEntry
                    entry={decision.entry}
                    helper={helper}
                    onResult={onResult}
                    onGeneration={onGeneration}
                    onPeek={onPeek}
                    peekId={decision.entry.kind === "ready" ? peekId : undefined}
                    framed={false}
                />
            </Stack>
        )
    }

    return (
        <View style={styles.root} testID={testID}>
            <Row gap="2" align="center" style={styles.head(toggleRoom)}>
                <ChampionAvatar name={helper} size={HEAD_SIZE} />
                <Stack gap="0" style={styles.flex}>
                    <Row gap="2" align="center">
                        <Text size="text-base" weight="bold" color="text-strong">
                            {TEXTS.title}
                        </Text>
                        {badge.count > 0 && (
                            <Badge text={cappedCount(badge.count)} variant={badge.urgent ? "danger" : "neutral"} />
                        )}
                    </Row>
                    {!single && (
                        <Text size="text-xs" color="text-muted" numberOfLines={1}>
                            {TEXTS.scope}
                        </Text>
                    )}
                </Stack>
                {onClose != null && <HeadButton iconName="times" iconSize={18} onPress={onClose} label={TEXTS.close} />}
            </Row>
            <ScrollView
                keyboardShouldPersistTaps="handled"
                style={styles.flexFill}
                contentContainerStyle={styles.scroll}
            >
                {badge.count === 0 ? (
                    <EmptyState title={TEXTS.calmTitle} message={TEXTS.calmMessage} iconName="check-circle" />
                ) : (
                    <Stack gap="3">
                        {alerts.length > 0 && (
                            <View style={styles.group("1")} accessibilityRole="list" accessibilityLabel={TEXTS.signals}>
                                {alerts.map((item) => (
                                    <TutorAlertRow
                                        key={item.id}
                                        item={item}
                                        onAction={(actionId) => onAction(item.id, actionId)}
                                        onDismiss={() => onDismiss(item.id)}
                                        onLook={look(item.student.id)}
                                        pendingActionId={pendingFor(item.id)}
                                    />
                                ))}
                            </View>
                        )}
                        {proposals.length > 0 && (
                            <View
                                style={styles.group("2")}
                                accessibilityRole="list"
                                accessibilityLabel={TEXTS.proposals}
                            >
                                {proposals.map(proposal)}
                            </View>
                        )}
                    </Stack>
                )}
            </ScrollView>
            {composer != null && (
                <View style={styles.composer}>
                    <TutorDraftComposer composer={composer} />
                </View>
            )}
        </View>
    )
}

/** Un geste de la tête de la colonne : une icône discrète. */
function HeadButton(props: { iconName: IconName; label: string; onPress: () => void; iconSize?: number }) {
    return (
        <IconButton
            iconName={props.iconName}
            appearance="transparent"
            iconColor="text-muted"
            size={32}
            iconSize={props.iconSize ?? 16}
            onPress={props.onPress}
            accessibilityLabel={props.label}
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    group: (gap: SpacingKey) => ({ gap: theme.spacing[gap] }),
    head: (toggleRoom: number | undefined) => ({
        minHeight: 44,
        paddingHorizontal: theme.spacing["1"],
        ...(toggleRoom != null && { paddingRight: toggleRoom }),
        paddingVertical: theme.spacing["1"],
        marginBottom: theme.spacing["2"],
        overflow: "hidden" as const,
    }),
    scroll: { paddingHorizontal: theme.spacing["1"], paddingBottom: theme.spacing["3"] },
    composer: {
        flexShrink: 0,
        paddingTop: theme.spacing["2"],
        paddingHorizontal: theme.spacing["1"],
        borderTopWidth: 1,
        borderTopColor: theme.colors["border-subtle"],
    },
    flex: { flex: 1, minWidth: 0 },
    flexFill: { flex: 1, minHeight: 0 },
}))
